import { usePathname, useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { InputSearch } from '@/components/form/InputSearch';

import { Button } from './Button';

interface SearchBarProps {
  onChange?: (value: string) => void;
  onSubmit?: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onReset?: () => void;
  setSearchText?: Dispatch<SetStateAction<string>>;
  placeholder?: string;
  searchText?: string;
  initialSearch?: boolean;
  disabled?: boolean;
}

export const SearchBar = ({
  onChange = () => null,
  onSubmit = () => null,
  onFocus = () => null,
  onBlur = () => null,
  onReset = () => null,
  setSearchText = () => null,
  placeholder = 'Rechercher',
  searchText = '',
  initialSearch,
  disabled,
}: SearchBarProps) => {
  const { formatMessage } = useIntl();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [initialized, setInitialized] = useState(false);

  const handleOnChange = (value: string) => {
    const params = new URLSearchParams();
    if (!value) {
      router.replace(pathname);
    }
    params.set('q', value);
    router.replace(`${pathname}?${params.toString()}`);
  };

  useEffect(() => {
    onChange(searchText);
  }, [searchText, onChange]);

  useEffect(() => {
    if (!router?.isReady || initialized) return;

    const currentSearchValue = searchParams.get('q') || '';
    setSearchText(currentSearchValue);
    if (initialSearch) {
      onChange(currentSearchValue);
    }
    setInitialized(true);
  }, [searchParams, initialSearch, router?.isReady, initialized, setSearchText, onChange]);

  const handleOnSubmit = () => onSubmit(searchText);

  const handleOnReset = () => {
    setSearchText('');
    onReset();
    router.replace(pathname);
  };

  return (
    <div className="w-full max-w-xl flex gap-3">
      <InputSearch
        value={searchText}
        placeholder={placeholder}
        onSearch={handleOnChange}
        onChange={setSearchText}
        onFocus={onFocus}
        onBlur={onBlur}
        onReset={handleOnReset}
        onSubmit={handleOnSubmit}
        disabled={disabled}
      />
      {searchText && (
        <>
          <Button
            onClick={handleOnSubmit}
            style="link"
            ariaLabel={formatMessage({ id: 'search.accessibility.submit' }, { values: searchText })}
            className="bg-brand-neutral-700 rounded-md m-0 p-0 h-10 hidden md:inline"
          >
            {formatMessage({ id: 'common.form.field.button.search' })}
          </Button>
          <Button
            onClick={handleOnReset}
            style="link"
            ariaLabel={formatMessage({ id: 'search.accessibility.abort' })}
            className="rounded-md m-0 p-0 h-10"
          >
            {formatMessage({ id: 'common.form.field.button.abort' })}
          </Button>
        </>
      )}
    </div>
  );
};
